import { dateUtils } from '@festina-life/core'
import { Alert, FullscreenSpinner } from '@festina-life/ui/feedback'
import { useTranslate } from '@festina-life/ui/hooks'
import { Stack } from '@festina-life/ui/structure'
import { Group, Panel, useDefaultLayout } from 'react-resizable-panels'

import { useConfigurationContext } from '../../context/configurationContext'
import { useNavigationParams } from '../../system/useNavigationParams'
import { useSpecId } from '../../system/useSpecId'
import { getReconciliationSpecOperationAtDate } from '../../uiModels/reconciliationSpecOperations/helpers'
import { useApplicationViewOperationsContext } from '../../workspace/applicationViewOperationsContext'
import { useOperationsContext } from '../../workspace/operationsContext'
import { ReconciliationSpecSidebarPanel } from './ReconciliationSpecSidebarPanel'
import { ReconciliationSpecViewerContent } from './ReconciliationSpecViewerContent'
import { ReconciliationSpecViewerTopBar } from './ReconciliationSpecViewerTopBar'

/**
 * Viewer for a single reconciliation-spec version, showing its metadata next to the
 * version history used to pick the version to view.
 */
export const ReconciliationSpecViewer = () => {
  const translate = useTranslate()
  const specId = useSpecId()
  const { block } = useNavigationParams()
  const { viewAt } = useConfigurationContext()
  const { isLoading } = useOperationsContext()
  const { defaultLayout, onLayoutChanged } = useDefaultLayout({
    groupId: 'reconciliationSpecViewerPanel',
    storage: localStorage,
  })
  const { applicationReconciliationSpecs } =
    useApplicationViewOperationsContext()
  const indexedReconciliationSpecOperations =
    applicationReconciliationSpecs.get(specId)
  const reconciliationSpecOperation = getReconciliationSpecOperationAtDate(
    indexedReconciliationSpecOperations,
    viewAt,
  )

  if (isLoading) {
    return <FullscreenSpinner position="relative" />
  }

  return (
    <Group
      orientation="horizontal"
      defaultLayout={defaultLayout}
      onLayoutChanged={onLayoutChanged}
      className="size-full"
    >
      <ReconciliationSpecSidebarPanel />
      <Panel
        className="size-full overflow-hidden"
        id="reconciliationSpecCentralView"
      >
        {reconciliationSpecOperation ? (
          <div className="grid size-full grid-rows-[auto_1fr] overflow-hidden">
            <ReconciliationSpecViewerTopBar
              reconciliationSpecOperation={reconciliationSpecOperation}
            />
            <ReconciliationSpecViewerContent
              reconciliationSpecOperation={reconciliationSpecOperation}
            />
          </div>
        ) : (
          <Stack className="size-full items-center justify-center">
            <Alert
              severity="error"
              title={translate(
                'reconciliationSpecViewer.noReconciliationSpec',
                { specId, block, viewAt: dateUtils.dateAsStr(viewAt) },
              )}
            />
          </Stack>
        )}
      </Panel>
    </Group>
  )
}
